import React from "react";
import {
  useFloating,
  autoUpdate,
  flip,
  offset,
  shift,
  useRole,
  useDismiss,
  useInteractions,
  useListNavigation,
  useTypeahead,
  FloatingPortal,
  FloatingFocusManager,
  FloatingOverlay,
} from "@floating-ui/react";
import { cn } from "@/lib/utils";

export const FloatingContextItem = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    label: string;
    disabled?: boolean;
    inset?: boolean;
  }
>(function FloatingContextItem({ label, disabled, inset, className, children, ...props }, ref) {
  return (
    <button
      {...props}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 truncate",
        inset && "pl-8",
        className,
      )}
      ref={ref}
      role="menuitem"
      disabled={disabled}
    >
      {children}
      {label}
    </button>
  );
});
FloatingContextItem.displayName = "FloatingContextItem";

export const FloatingContextLabel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    inset?: boolean;
  }
>(function FloatingContextLabel({ className, inset, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        "px-2 py-1.5 text-sm font-semibold text-foreground",
        inset && "pl-8",
        className,
      )}
      {...props}
    />
  );
});
FloatingContextLabel.displayName = "FloatingContextLabel";

export const FloatingContextSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function FloatingContextSeparator({ className, ...props }, ref) {
  return <div ref={ref} className={cn("-mx-1 my-1 h-px bg-border", className)} {...props} />;
});
FloatingContextSeparator.displayName = "FloatingContextSeparator";

interface FloatingContextProps {
  children: React.ReactNode;
  trigger: React.ReactNode;
  triggerClassName?: string;
}

export const FloatingContext = React.forwardRef<HTMLButtonElement, FloatingContextProps>(
  function FloatingContext({ children, trigger, triggerClassName }, _forwardedRef) {
    const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const [position, setPosition] = React.useState({ x: 0, y: 0 });

    const listItemsRef = React.useRef<Array<HTMLButtonElement | null>>([]);
    const listContentRef = React.useRef(
      React.Children.map(children, (child) =>
        React.isValidElement(child) ? child.props.label : null,
      ) as Array<string | null>,
    );

    const { refs, context } = useFloating({
      open: isOpen,
      onOpenChange: setIsOpen,
      middleware: [
        offset(5),
        flip({
          fallbackPlacements: ["top-start", "left-start"],
        }),
        shift({ padding: 10 }),
      ],
      placement: "bottom-start",
      strategy: "fixed",
      whileElementsMounted: autoUpdate,
    });

    const role = useRole(context, { role: "menu" });
    const dismiss = useDismiss(context);
    const listNavigation = useListNavigation(context, {
      listRef: listItemsRef,
      onNavigate: setActiveIndex,
      activeIndex,
    });
    const typeahead = useTypeahead(context, {
      enabled: isOpen,
      listRef: listContentRef,
      onMatch: setActiveIndex,
      activeIndex,
    });

    const { getFloatingProps, getItemProps } = useInteractions([
      role,
      dismiss,
      listNavigation,
      typeahead,
    ]);

    const handleContextMenu = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const x = e.clientX;
      const y = e.clientY;
      setPosition({ x, y });
      setIsOpen(true);
    };

    React.useEffect(() => {
      const handleClickOutside = () => setIsOpen(false);
      if (isOpen) {
        document.addEventListener("click", handleClickOutside);
      }
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }, [isOpen]);

    return (
      <>
        <div ref={refs.setReference} onContextMenu={handleContextMenu} className={triggerClassName}>
          {trigger}
        </div>
        <FloatingPortal>
          {isOpen && (
            <FloatingOverlay lockScroll style={{ zIndex: 9999 }}>
              <FloatingFocusManager context={context} initialFocus={refs.floating}>
                <div
                  className={cn(
                    "fixed min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 focus:outline-hidden",
                    "text-popover-foreground shadow-md",
                    "data-[state=open]:animate-in data-[state=closed]:animate-out",
                    "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                    "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                    "data-[side=bottom]:slide-in-from-top-2",
                    "data-[side=left]:slide-in-from-right-2",
                    "data-[side=right]:slide-in-from-left-2",
                    "data-[side=top]:slide-in-from-bottom-2",
                  )}
                  ref={refs.setFloating}
                  style={{
                    position: "fixed",
                    left: position.x,
                    top: position.y,
                    margin: 0,
                    transform: "none",
                  }}
                  {...getFloatingProps()}
                >
                  {React.Children.map(
                    children,
                    (child, index) =>
                      React.isValidElement(child) &&
                      React.cloneElement(
                        child,
                        getItemProps({
                          tabIndex: activeIndex === index ? 0 : -1,
                          ref(node: HTMLButtonElement) {
                            listItemsRef.current[index] = node;
                          },
                          onClick() {
                            child.props.onClick?.();
                            setIsOpen(false);
                          },
                          onMouseUp() {
                            child.props.onClick?.();
                            setIsOpen(false);
                          },
                        }),
                      ),
                  )}
                </div>
              </FloatingFocusManager>
            </FloatingOverlay>
          )}
        </FloatingPortal>
      </>
    );
  },
);
FloatingContext.displayName = "FloatingContext";
